
import React, { useState, useEffect } from "react";
import { Part, PartFilter } from "@/data/parts";
import PartCard from "./PartCard";
import { CollapsibleProvider } from "./part-card/collapsible-context/CollapsibleContext";

type SortOption = "none" | "rating-high" | "rating-low";

interface PartGridProps {
  parts: Part[];
  activeFilters: PartFilter[];
  sortBy: SortOption;
  setSortBy: (option: SortOption) => void;
}

const PartGrid = ({ parts, activeFilters, sortBy, setSortBy }: PartGridProps) => {
  // Track window size for responsive adjustments
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  
  // Update window width on resize
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Initial check
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Function to toggle sorting by rating
  const toggleRatingSort = () => {
    if (sortBy === "rating-high") {
      setSortBy("rating-low");
    } else if (sortBy === "rating-low") {
      setSortBy("none");
    } else {
      setSortBy("rating-high");
    }
  };

  if (parts.length === 0) {
    return (
      <div className="text-center py-16 w-full">
        <h3 className="text-xl font-medium">No parts found</h3>
        <p className="text-muted-foreground mt-2">
          Try adjusting your search or filters to find what you're looking for.
        </p>
      </div>
    );
  }

  // Calculate optimal grid layout based on window width
  const getColumnClass = () => {
    if (windowWidth < 640) return "grid-cols-1";
    if (windowWidth < 768) return "grid-cols-1";
    if (windowWidth < 1024) return "grid-cols-1 md:grid-cols-2";
    if (windowWidth < 1280) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    if (windowWidth < 1536) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5";
  };

  return (
    <CollapsibleProvider>
      <div className={`grid ${getColumnClass()} gap-5 w-full`}>
        {parts.map((part) => (
          <div key={part.id} className="flex w-full">
            <PartCard 
              part={part} 
              activeFilters={activeFilters}
              sortBy={sortBy}
              onRatingClick={toggleRatingSort}
            />
          </div>
        ))}
      </div>
    </CollapsibleProvider>
  );
};

export default PartGrid;
